<script setup lang="ts">
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"

import {
  Button,
  FormAlert,
  Label,
  Select,
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Textarea,
  TimeRange,
} from "@/shared/ui"
import { buildBlockRange } from "../model/timeRange"
import { useRecordsStore } from "../model/store"

const store = useRecordsStore()
const { t } = useI18n()

const open = computed(() => store.activeSheet === "createBlock")

// --- Local form state ---
const reason = ref("")
const selectedMasterId = ref<string | null>(null)

const BLOCK_DEFAULT_START = 480 // 08:00 fallback when no cell context
const BLOCK_DURATION = 60

function initialRange(): { start: number; end: number } {
  const start = store.createBlockStartMin ?? BLOCK_DEFAULT_START
  return { start, end: Math.min(start + BLOCK_DURATION, 1440) }
}

const range = ref(initialRange())

// --- Derived ---

const showMasterSelect = computed(() => store.createBlockMasterId === undefined)

const masterOptions = computed(() =>
  store.workingMasters
    .filter((m) => m.intervals.length > 0)
    .map((m) => ({ value: m.id, label: m.name })),
)

const canSubmit = computed(() => {
  const masterId = store.createBlockMasterId ?? selectedMasterId.value
  return !!masterId && store.blockMutationStatus !== "pending"
})

const inlineError = computed(() => {
  const key = store.blockErrorKey
  return key ? t(key) : undefined
})

// --- W1: reset local form on open (immediate) ---
watch(
  open,
  (isOpen) => {
    if (!isOpen) return
    reason.value = ""
    range.value = initialRange()
    selectedMasterId.value = null
  },
  { immediate: true },
)

// --- Submit ---

async function handleSubmit(): Promise<void> {
  const masterId = store.createBlockMasterId ?? selectedMasterId.value
  if (!masterId) return
  const { startsAt, endsAt } = buildBlockRange(store.selectedDate, range.value, store.salonTz)
  try {
    await store.submitCreateBlock({
      masterId,
      startsAt,
      endsAt,
      reason: reason.value.trim() || undefined,
    })
  } catch {
    // inline errors: store sets blockErrorKey → FormAlert re-renders reactively
    // toast errors: store fires toast directly
  }
}
</script>

<template>
  <Sheet
    :open="open"
    @update:open="(v) => { if (!v) store.closeSheet() }"
  >
    <SheetContent
      side="bottom"
      :close-aria-label="t('journal.timeBlock.close')"
    >
      <SheetHeader>
        <SheetTitle>{{ t("journal.timeBlock.createTitle") }}</SheetTitle>
      </SheetHeader>

      <!-- Inline error -->
      <FormAlert
        v-if="inlineError"
        data-testid="form-error"
        variant="destructive"
        :message="inlineError"
      />

      <!-- Master select — only when no column context -->
      <div
        v-if="showMasterSelect"
        class="create-block-sheet__field"
      >
        <Label>{{ t("journal.timeBlock.masterLabel") }}</Label>
        <Select
          v-model="selectedMasterId"
          :options="masterOptions"
          :placeholder="t('journal.timeBlock.selectMaster')"
          :disabled="store.blockMutationStatus === 'pending'"
        />
      </div>

      <!-- Time range -->
      <div class="create-block-sheet__field">
        <Label>{{ t("journal.timeBlock.rangeLabel") }}</Label>
        <TimeRange
          v-model="range"
          :step="15"
          :min="0"
          :max="1440"
          :disabled="store.blockMutationStatus === 'pending'"
        />
      </div>

      <!-- Reason -->
      <div class="create-block-sheet__field">
        <Label>{{ t("journal.timeBlock.reasonLabel") }}</Label>
        <Textarea
          v-model="reason"
          :maxlength="200"
          :placeholder="t('journal.timeBlock.reasonPlaceholder')"
          :disabled="store.blockMutationStatus === 'pending'"
        />
      </div>

      <SheetFooter>
        <Button
          type="button"
          :loading="store.blockMutationStatus === 'pending'"
          :disabled="!canSubmit"
          @click="handleSubmit"
        >
          {{ t("journal.timeBlock.submit") }}
        </Button>
      </SheetFooter>
    </SheetContent>
  </Sheet>
</template>

<style scoped>
.create-block-sheet__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 12px;
}
</style>
